import { supabase } from "@/integrations/supabase/client";

export type MemberRole =
  | "treasury_secretary"
  | "managing_member"
  | "board_member_active"
  | "board_member_passive"
  | "financial_specialist"
  | "legal_specialist"
  | "tax_specialist"
  | "operations_manager"
  | "advisor"
  | "observer";

export type Permission =
  | "tasks.view_all"
  | "tasks.view_assigned"
  | "tasks.create"
  | "tasks.update_own"
  | "tasks.update_assigned"
  | "tasks.update_any"
  | "tasks.delete"
  | "tasks.assign"
  | "tasks.comment"
  | "members.view"
  | "members.invite"
  | "members.edit"
  | "members.remove"
  | "members.change_roles"
  | "assets.view"
  | "assets.manage"
  | "documents.view"
  | "documents.upload"
  | "documents.sign"
  | "documents.delete"
  | "financials.view"
  | "financials.edit"
  | "settings.view"
  | "settings.edit"
  | "audit.view";

// Permission matrix: which roles have which permissions
export const PERMISSIONS: Record<Permission, MemberRole[]> = {
  "tasks.view_all": ["treasury_secretary", "managing_member", "operations_manager"],
  "tasks.view_assigned": [
    "treasury_secretary",
    "managing_member",
    "board_member_active",
    "board_member_passive",
    "financial_specialist",
    "legal_specialist",
    "tax_specialist",
    "operations_manager",
    "advisor",
    "observer",
  ],
  "tasks.create": [
    "treasury_secretary",
    "managing_member",
    "board_member_active",
    "operations_manager",
  ],
  "tasks.update_own": [
    "treasury_secretary",
    "managing_member",
    "board_member_active",
    "board_member_passive",
    "financial_specialist",
    "legal_specialist",
    "tax_specialist",
    "operations_manager",
    "advisor",
    "observer",
  ],
  "tasks.update_assigned": [
    "treasury_secretary",
    "managing_member",
    "board_member_active",
    "operations_manager",
    "financial_specialist",
    "legal_specialist",
    "tax_specialist",
  ],
  "tasks.update_any": ["treasury_secretary", "managing_member"],
  "tasks.delete": ["treasury_secretary", "managing_member"],
  "tasks.assign": ["treasury_secretary", "managing_member", "operations_manager"],
  "tasks.comment": [
    "treasury_secretary",
    "managing_member",
    "board_member_active",
    "board_member_passive",
    "financial_specialist",
    "legal_specialist",
    "tax_specialist",
    "operations_manager",
    "advisor",
    "observer",
  ],
  "members.view": [
    "treasury_secretary",
    "managing_member",
    "board_member_active",
    "board_member_passive",
    "financial_specialist",
    "legal_specialist",
    "tax_specialist",
    "operations_manager",
    "advisor",
    "observer",
  ],
  "members.invite": ["treasury_secretary", "managing_member"],
  "members.edit": ["treasury_secretary"],
  "members.remove": ["treasury_secretary"],
  "members.change_roles": ["treasury_secretary"],
  "assets.view": [
    "treasury_secretary",
    "managing_member",
    "financial_specialist",
    "tax_specialist",
  ],
  "assets.manage": ["treasury_secretary", "managing_member", "financial_specialist"],
  "documents.view": [
    "treasury_secretary",
    "managing_member",
    "board_member_active",
    "board_member_passive",
    "financial_specialist",
    "legal_specialist",
    "tax_specialist",
    "operations_manager",
    "advisor",
    "observer",
  ],
  "documents.upload": ["treasury_secretary", "managing_member", "legal_specialist"],
  "documents.sign": [
    "treasury_secretary",
    "managing_member",
    "board_member_active",
    "board_member_passive",
    "financial_specialist",
    "legal_specialist",
    "tax_specialist",
    "operations_manager",
  ],
  "documents.delete": ["treasury_secretary", "managing_member"],
  "financials.view": [
    "treasury_secretary",
    "managing_member",
    "financial_specialist",
    "tax_specialist",
  ],
  "financials.edit": ["treasury_secretary", "financial_specialist"],
  "settings.view": ["treasury_secretary", "managing_member"],
  "settings.edit": ["treasury_secretary"],
  "audit.view": ["treasury_secretary"],
};

// Role display information
export const ROLE_INFO: Record<
  MemberRole,
  { name: string; description: string; color: string }
> = {
  treasury_secretary: {
    name: "Secretary Treasurer",
    description: "Full administrative access to all UNA operations and settings",
    color: "#D4AF37", // Gold
  },
  managing_member: {
    name: "Managing Member",
    description:
      "Can manage all operations except member roles and critical settings",
    color: "#9333EA", // Purple
  },
  board_member_active: {
    name: "Active Board Member",
    description: "Active participation with ability to create and manage tasks",
    color: "#3B82F6", // Blue
  },
  board_member_passive: {
    name: "Passive Board Member",
    description: "View access with ability to sign documents and attend meetings",
    color: "#6B7280", // Gray
  },
  financial_specialist: {
    name: "Financial Specialist",
    description: "Manages financial operations, assets, and contributions",
    color: "#10B981", // Green
  },
  legal_specialist: {
    name: "Legal Specialist",
    description: "Handles legal documents, compliance, and resolutions",
    color: "#EF4444", // Red
  },
  tax_specialist: {
    name: "Tax Specialist",
    description: "Manages tax filings, reporting, and compliance",
    color: "#F59E0B", // Orange
  },
  operations_manager: {
    name: "Operations Manager",
    description: "Oversees day-to-day operations and task management",
    color: "#06B6D4", // Cyan
  },
  advisor: {
    name: "Advisor",
    description: "Read-only advisory access to provide guidance",
    color: "#6366F1", // Indigo
  },
  observer: {
    name: "Observer",
    description: "Limited read-only access for monitoring purposes",
    color: "#64748B", // Slate
  },
};

// Get user's role in a specific UNA
export async function getUserRole(unaId: string): Promise<MemberRole | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  // Check if user is treasury secretary
  const { data: una } = await supabase
    .from("unas")
    .select("treasury_secretary_id")
    .eq("id", unaId)
    .single();

  if (una?.treasury_secretary_id === user.id) {
    return "treasury_secretary";
  }

  // Check if user is a member
  const { data: member } = await supabase
    .from("una_members")
    .select("member_role")
    .eq("una_id", unaId)
    .eq("user_id", user.id)
    .eq("status", "active")
    .single();

  return member?.member_role as MemberRole | null;
}

// Check if user has a specific permission
export async function hasPermission(
  unaId: string,
  permission: Permission
): Promise<boolean> {
  const role = await getUserRole(unaId);
  if (!role) return false;

  return PERMISSIONS[permission].includes(role);
}

// Check if user has all of the specified permissions
export async function hasAllPermissions(
  unaId: string,
  permissions: Permission[]
): Promise<boolean> {
  const role = await getUserRole(unaId);
  if (!role) return false;

  return permissions.every((permission) =>
    PERMISSIONS[permission].includes(role)
  );
}

// Check if user has any of the specified permissions
export async function hasAnyPermission(
  unaId: string,
  permissions: Permission[]
): Promise<boolean> {
  const role = await getUserRole(unaId);
  if (!role) return false;

  return permissions.some((permission) =>
    PERMISSIONS[permission].includes(role)
  );
}

// Get friendly display name for a role
export function getRoleDisplayName(role: MemberRole): string {
  return ROLE_INFO[role].name;
}

// Get description for a role
export function getRoleDescription(role: MemberRole): string {
  return ROLE_INFO[role].description;
}

// Get color for a role
export function getRoleColor(role: MemberRole): string {
  return ROLE_INFO[role].color;
}

// Log audit action
export async function logAuditAction(
  unaId: string,
  action: string,
  resourceType: "task" | "asset" | "member" | "document" | "contribution" | "resolution" | "invitation",
  resourceId?: string,
  details?: Record<string, any>
) {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  const role = await getUserRole(unaId);

  await supabase.from("una_audit_log").insert({
    una_id: unaId,
    user_id: user.id,
    member_role: role,
    action,
    resource_type: resourceType,
    resource_id: resourceId,
    details: details || {},
  });
}

// Get suggested assignees for a task category
export function getSuggestedRolesForCategory(category: string): MemberRole[] {
  const suggestions: Record<string, MemberRole[]> = {
    asset: ["financial_specialist", "managing_member", "treasury_secretary"],
    contribution: ["financial_specialist", "tax_specialist", "treasury_secretary"],
    resolution: ["legal_specialist", "managing_member", "board_member_active"],
    documentation: ["legal_specialist", "operations_manager", "managing_member"],
    banking: ["financial_specialist", "treasury_secretary"],
    compliance: ["legal_specialist", "tax_specialist", "managing_member"],
    governance: ["managing_member", "board_member_active", "treasury_secretary"],
    operations: ["operations_manager", "managing_member"],
  };

  return suggestions[category.toLowerCase()] || ["managing_member", "board_member_active"];
}
